import { useState } from "react";
import { HiOutlineCheckCircle, HiOutlineExclamationCircle } from "react-icons/hi";
import Files from "lib/api/files";
import { CalibrationResults } from "lib/constants/Calibration";
import handlePopupFlagError from "lib/errors/handlePopupFlagError";
import { useDispatch } from "store";
import { createCalibrationCertificate, importCalibrationCertificate } from "store/reducers";
import { FixLater, TAsset, TImportedCalibrationCertificate } from "types";
import { PrimaryButton, SecondaryButton } from "components/Buttons";
import { DateInput, FileInput, NumberInput, SelectInput, TextInput } from "components/Input";
import Loading from "components/Loading";
import FieldGroup from "components/form/FieldGroup/FieldGroup";
import { Modal, ModalContent, ModalFooter, ModalHeader } from "components/modals/Modal";
// Imported from their own modules rather than the components/shared barrel, which
// pulls in a modal cycle when this component is rendered on its own.
import { VSpace } from "components/shared/layouts/VSpace/VSpace";
import { TextBody } from "components/shared/typography/TextBody/TextBody";
import { toISODate } from "./calibrationImportValues";

type Stage = "upload" | "processing" | "review" | "saving";

// The extracted values are edited before saving, so dates arrive as ISO strings from
// the model but come back as moments once the user picks a new one.
type ReviewValues = Omit<TImportedCalibrationCertificate, "dateCalibrated" | "dateNextDue"> & {
  dateCalibrated: FixLater;
  dateNextDue: FixLater;
};

export const ImportCalibrationModal = ({ modal, onClose }: FixLater) => {
  const dispatch = useDispatch();
  const { asset, onSaved }: { asset: TAsset; onSaved: () => void } = modal.props;
  const [stage, setStage] = useState<Stage>("upload");
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [values, setValues] = useState<ReviewValues | null>(null);

  const toolName = asset.description || asset.catalogProduct?.name || `Asset #${asset.id}`;

  const handleUpload = async (_: string, uploaded: File[]) => {
    const file = uploaded?.[0];
    if (!file) return;
    setFiles(uploaded);
    setError(null);
    setStage("processing");
    try {
      const result = await dispatch(importCalibrationCertificate({ assetId: asset.guid, file })).unwrap();
      setValues(result);
      setStage("review");
    } catch (err: FixLater) {
      setError(err?.message ?? "Failed to extract certificate data. Please try again.");
      setFiles([]);
      setStage("upload");
    }
  };

  const handleChange = (name: string, value: FixLater) =>
    setValues((previous) => (previous ? { ...previous, [name]: value } : previous));

  // The uploaded document is the certificate's evidence, so it is stored with the
  // record rather than discarded once the fields have been read out of it. Uploading
  // here rather than at selection keeps an abandoned review from leaving orphaned
  // files behind.
  const uploadAttachments = async (): Promise<number[] | undefined> => {
    if (!files.length) return undefined;
    const uploaded = await Promise.all(files.map((file) => Files.upload(file)));
    return uploaded.map((file) => file.id);
  };

  const handleSave = async () => {
    if (!values) return;
    setStage("saving");
    try {
      const attachments = await uploadAttachments();
      await dispatch(
        createCalibrationCertificate({
          assetId: asset.guid,
          attachments,
          certificateNumber: values.certificateNumber,
          result: values.result,
          dateCalibrated: toISODate(values.dateCalibrated) as string,
          dateNextDue: toISODate(values.dateNextDue),
          performedBy: values.performedBy || undefined,
          procedureNumber: values.procedureNumber || undefined,
          temperature: values.temperature ? Number(values.temperature) : undefined,
          humidity: values.humidity ? Number(values.humidity) : undefined,
          tolerance: values.tolerance ? Number(values.tolerance) : undefined,
        }),
      ).unwrap();
      onSaved();
      onClose();
    } catch (err) {
      handlePopupFlagError(err);
      setStage("review");
    }
  };

  const handleUploadAnother = () => {
    setValues(null);
    setFiles([]);
    setStage("upload");
  };

  const renderContent = () => {
    if (stage === "processing") {
      return (
        <VSpace className="items-center">
          <Loading size="large" />
          <TextBody>Extracting certificate data with AI&hellip;</TextBody>
        </VSpace>
      );
    }

    if (stage === "upload") {
      return (
        <VSpace>
          <FileInput
            input={{
              name: "file",
              placeholder: "Upload a certificate or drag and drop",
              accept: "application/pdf,image/*",
            }}
            value={files}
            onChange={handleUpload}
          />
          <TextBody className="text-center text-sm text-gray-400">PDF or image (JPG, PNG) — up to 20 MB</TextBody>
          {error && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              <HiOutlineExclamationCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              {error}
            </div>
          )}
        </VSpace>
      );
    }

    return (
      <VSpace gap="gap-4">
        <div className="flex items-center gap-2 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">
          <HiOutlineCheckCircle className="h-4 w-4 flex-shrink-0" />
          Certificate data extracted. Review and save.
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 max-h-96 overflow-y-auto pr-1">
          <FieldGroup label="Certificate number">
            <TextInput
              input={{ name: "certificateNumber", placeholder: "Certificate number" }}
              value={values?.certificateNumber ?? ""}
              onChange={handleChange}
            />
          </FieldGroup>

          <FieldGroup label="Result">
            <SelectInput
              input={{
                name: "result",
                options: CalibrationResults.items,
                clearable: false,
                isSearchable: false,
                placeholder: "Select a result",
              }}
              value={values?.result ?? ""}
              onChange={(name: string, option: FixLater) => handleChange(name, option?.value)}
            />
          </FieldGroup>

          <FieldGroup label="Date calibrated">
            <DateInput
              input={{ name: "dateCalibrated", placeholder: "Pick a date" }}
              value={values?.dateCalibrated ?? ""}
              onChange={handleChange}
            />
          </FieldGroup>

          <FieldGroup label="Next due date">
            <DateInput
              input={{ name: "dateNextDue", placeholder: "Pick a date" }}
              value={values?.dateNextDue ?? ""}
              onChange={handleChange}
            />
          </FieldGroup>

          <FieldGroup label="Performed by">
            <TextInput
              input={{ name: "performedBy", placeholder: "Who performed the calibration?" }}
              value={values?.performedBy ?? ""}
              onChange={handleChange}
            />
          </FieldGroup>

          <FieldGroup label="Procedure number">
            <TextInput
              input={{ name: "procedureNumber", placeholder: "Procedure number" }}
              value={values?.procedureNumber ?? ""}
              onChange={handleChange}
            />
          </FieldGroup>

          <FieldGroup label="Temperature (°C)">
            <NumberInput
              input={{ name: "temperature", step: "0.01", placeholder: "Measured temperature" }}
              value={values?.temperature ?? ""}
              onChange={handleChange}
            />
          </FieldGroup>

          <FieldGroup label="Humidity (%)">
            <NumberInput
              input={{ name: "humidity", step: "0.01", placeholder: "Measured humidity" }}
              value={values?.humidity ?? ""}
              onChange={handleChange}
            />
          </FieldGroup>

          <FieldGroup label="Tolerance +/-">
            <NumberInput
              input={{ name: "tolerance", step: "0.01", placeholder: "Enter tolerance" }}
              value={values?.tolerance ?? ""}
              onChange={handleChange}
            />
          </FieldGroup>
        </div>
      </VSpace>
    );
  };

  const renderFooter = () => {
    if (stage === "upload" || stage === "processing") {
      return <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>;
    }

    return (
      <div className="flex flex-row flex-1 items-center justify-between">
        <SecondaryButton disabled={stage === "saving"} onClick={handleUploadAnother}>
          Upload different file
        </SecondaryButton>
        <div className="flex gap-2">
          <PrimaryButton submitting={stage === "saving"} onClick={handleSave}>
            Save certificate
          </PrimaryButton>
          <SecondaryButton disabled={stage === "saving"} onClick={onClose}>
            Cancel
          </SecondaryButton>
        </div>
      </div>
    );
  };

  return (
    <Modal>
      <ModalHeader title="AI certificate import">
        <TextBody className="text-gray-500">{toolName}</TextBody>
      </ModalHeader>
      <ModalContent>{renderContent()}</ModalContent>
      <ModalFooter>{renderFooter()}</ModalFooter>
    </Modal>
  );
};
